import { useCallback, useEffect, useRef, useState } from "react";
import { accountService } from "../services/accountService";

const GEO_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 6000,
  maximumAge: 60000,
};

function normalizeCoords(lat, lng) {
  return {
    lat: Number(Number(lat).toFixed(6)),
    lng: Number(Number(lng).toFixed(6)),
  };
}

export function useGeolocation({ autoLocate = true, persist = true } = {}) {
  const [position, setPosition] = useState(null);
  const [locating, setLocating] = useState(false);
  const [denied, setDenied] = useState(false);
  const [usingSaved, setUsingSaved] = useState(false);
  const savedLoadedRef = useRef(false);

  const applyPosition = useCallback(
    async (next, { fromSaved = false } = {}) => {
      setPosition(next);
      setDenied(false);
      setUsingSaved(fromSaved);

      if (!fromSaved && persist) {
        try {
          await accountService.saveLocation(next);
          setUsingSaved(false);
        } catch {
          // Mantener la posición en memoria aunque falle la persistencia remota.
        }
      }
    },
    [persist]
  );

  const loadSavedLocation = useCallback(async () => {
    try {
      const saved = await accountService.getSavedLocation();
      if (saved?.lat != null && saved?.lng != null) {
        await applyPosition(
          { lat: saved.lat, lng: saved.lng },
          { fromSaved: true }
        );
        return true;
      }
    } catch {
      // Sin ubicación guardada.
    }
    return false;
  }, [applyPosition]);

  const locate = useCallback(
    async ({ preferSaved = true } = {}) => {
      if (preferSaved && !savedLoadedRef.current) {
        savedLoadedRef.current = true;
        await loadSavedLocation();
      }

      if (!("geolocation" in navigator)) {
        setDenied(true);
        setLocating(false);
        return;
      }

      setLocating(true);
      setDenied(false);

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const next = normalizeCoords(
            pos.coords.latitude,
            pos.coords.longitude
          );
          await applyPosition(next);
          setLocating(false);
        },
        () => {
          setDenied(true);
          setLocating(false);
        },
        GEO_OPTIONS
      );
    },
    [applyPosition, loadSavedLocation]
  );

  useEffect(() => {
    if (!autoLocate) return;
    locate();
    // Solo al montar: evita re-solicitar en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    position,
    locating,
    denied,
    usingSaved,
    locate,
    loadSavedLocation,
  };
}
